
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, Globe, Zap, Check } from 'lucide-react';

const ResidentialTrustSection = () => {
  const trustFeatures = [
    {
      icon: Shield,
      title: 'Direct relationships with providers—no brokers involved',
      description: 'We maintain direct contracts with ISPs for maximum reliability'
    },
    {
      icon: Globe,
      title: 'Consistent quality and clean IP reputation',
      description: 'Our IPs are maintained to the highest standards with regular monitoring'
    },
    {
      icon: Zap,
      title: 'Infrastructure monitored and managed in-house',
      description: '24/7 monitoring ensures optimal performance and uptime'
    },
    {
      icon: Check,
      title: 'Zero recycled or abused IPs',
      description: 'Fresh, clean IPs that maintain their reputation and effectiveness'
    }
  ];

  return (
    <section className="py-20 bg-gray-50 relative section-pulse overflow-hidden">
      {/* Data Flow Lines converging to "Our" */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Diagonal lines flowing toward center from different angles */}
        <div className="absolute top-1/4 left-0 w-32 h-px bg-gradient-to-r from-nexx-green/60 to-transparent rotate-12 origin-left animate-flow-to-center" style={{ animationDelay: '0s' }}></div>
        <div className="absolute top-1/3 left-0 w-40 h-px bg-gradient-to-r from-blue-500/40 to-transparent rotate-6 origin-left animate-flow-to-center" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute top-1/2 left-0 w-36 h-px bg-gradient-to-r from-green-400/50 to-transparent rotate-3 origin-left animate-flow-to-center" style={{ animationDelay: '1s' }}></div>
        
        {/* Lines from right side */}
        <div className="absolute top-1/4 right-0 w-32 h-px bg-gradient-to-l from-nexx-green/60 to-transparent -rotate-12 origin-right animate-flow-to-center" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute top-1/3 right-0 w-40 h-px bg-gradient-to-l from-blue-500/40 to-transparent -rotate-6 origin-right animate-flow-to-center" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 right-0 w-36 h-px bg-gradient-to-l from-green-400/50 to-transparent -rotate-3 origin-right animate-flow-to-center" style={{ animationDelay: '2.5s' }}></div>
        
        {/* Lines from top */}
        <div className="absolute top-0 left-1/4 w-px h-24 bg-gradient-to-b from-nexx-green/60 to-transparent rotate-12 origin-top animate-flow-to-center" style={{ animationDelay: '0.8s' }}></div>
        <div className="absolute top-0 left-1/3 w-px h-32 bg-gradient-to-b from-blue-500/40 to-transparent rotate-6 origin-top animate-flow-to-center" style={{ animationDelay: '1.3s' }}></div>
        <div className="absolute top-0 right-1/3 w-px h-28 bg-gradient-to-b from-green-400/50 to-transparent -rotate-6 origin-top animate-flow-to-center" style={{ animationDelay: '1.8s' }}></div>
        <div className="absolute top-0 right-1/4 w-px h-24 bg-gradient-to-b from-nexx-green/60 to-transparent -rotate-12 origin-top animate-flow-to-center" style={{ animationDelay: '2.3s' }}></div>
        
        {/* Data packets flowing along the lines */}
        <div className="absolute top-1/4 left-8 w-2 h-2 bg-nexx-green rounded-full animate-flow-to-center" style={{ animationDelay: '0.2s' }}></div>
        <div className="absolute top-1/3 left-12 w-1.5 h-1.5 bg-blue-500 rounded-full animate-flow-to-center" style={{ animationDelay: '0.7s' }}></div>
        <div className="absolute top-1/2 left-6 w-1 h-1 bg-green-400 rounded-full animate-flow-to-center" style={{ animationDelay: '1.2s' }}></div>
        <div className="absolute top-1/4 right-8 w-2 h-2 bg-nexx-green rounded-full animate-flow-to-center" style={{ animationDelay: '1.7s' }}></div>
        <div className="absolute top-1/3 right-12 w-1.5 h-1.5 bg-blue-500 rounded-full animate-flow-to-center" style={{ animationDelay: '2.2s' }}></div>
        <div className="absolute top-8 left-1/4 w-1.5 h-1.5 bg-nexx-green rounded-full animate-flow-to-center" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-6 right-1/3 w-1 h-1 bg-green-400 rounded-full animate-flow-to-center" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            We're Not Resellers. This Is{' '}
            <span className="relative text-nexx-green">
              <span 
                className="relative z-10"
                style={{
                  textShadow: '0 0 20px rgba(34, 197, 94, 0.6), 0 0 40px rgba(34, 197, 94, 0.4), 0 0 60px rgba(34, 197, 94, 0.2)',
                  animation: 'pulse 2s ease-in-out infinite'
                }}
              >
                Our
              </span>
              <div className="absolute -inset-4 bg-nexx-green/20 rounded-lg animate-ping" style={{ animationDuration: '3s' }}></div>
              <div className="absolute -inset-2 bg-nexx-green/10 rounded-lg animate-pulse"></div>
            </span>{' '}
            Network.
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            Our proxy infrastructure is built on direct ISP contracts. That means stability you can count on and access you can trust.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {trustFeatures.map((feature, index) => (
            <div
              key={feature.title}
              className="flex items-start space-x-4 p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 animate-fade-in"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="w-12 h-12 bg-nexx-green/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <feature.icon className="h-6 w-6 text-nexx-green" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Badge CTA */}
        <div className="text-center bg-white rounded-2xl p-8 shadow-lg border-2 border-nexx-green/20">
          <div className="flex items-center justify-center mb-4">
            <Check className="h-8 w-8 text-nexx-green mr-3" />
            <h3 className="text-2xl font-bold text-gray-900">
              Built on Real Contracts, Not Loopholes
            </h3>
          </div>
          <p className="text-xl text-gray-600 mb-6">
            Ready to Try Residential Proxies That Actually Deliver?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-nexx-green hover:bg-green-700 text-white px-8 py-4 text-lg group"
            >
              Start a Free Trial
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-nexx-green text-nexx-green hover:bg-nexx-green hover:text-white px-8 py-4 text-lg"
            >
              Talk to an Engineer
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResidentialTrustSection;
